import { useQuery, useQueries, UseQueryOptions } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import client from "../../api";
import { defaultQueryParams, QueryKey } from "../../utils/api";
import { toProcessWithSteps } from "../../formspec/utils/conditions";

/**
 * Fetch all conditions for a given experiment ID
 */
export const useGetConditionsFromExp = <TData = FormspecCondition[]>(
  queryOptions?: Partial<UseQueryOptions<FormspecCondition[], Error, TData>>
) => {
  const { exp_id } = useParams();

  return useQuery<FormspecCondition[], Error, TData>({
    queryKey: [QueryKey.Conditions, exp_id],
    queryFn: async () =>
      await client.get(`meta/experiments/${exp_id}/conditions/external`),
    ...defaultQueryParams,
    ...queryOptions,
  });
};

/**
 * Fetch constraints for multiple items from FormSpec
 * Uses individual queries for better caching - each item's constraints are cached independently
 */
export const useGetItemsConstraints = (itemUuids: string[]) => {
  const queries = useQueries({
    queries: itemUuids.map((itemUuid) => ({
      queryKey: [QueryKey.Items, itemUuid, "constraints"],
      queryFn: async (): Promise<ConstraintsResponse> => {
        return await client.get(`formspec/items/${itemUuid}/constraints`);
      },
      enabled: !!itemUuid,
      ...defaultQueryParams,
    })),
  });

  return {
    constraints: queries
      .map((q) => q.data)
      .filter((c): c is ConstraintsResponse => !!c),
    queries: queries,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error).filter((e): e is Error => !!e),
  };
};

/**
 * Fetch the constraint validation result for a single item from FormSpec.
 *
 * Validation is owned by the backend. The frontend should never need to
 * trigger validation directly: it is attached to single-save responses, and
 * fetched here on demand only when no cached value exists for the item
 * (e.g. first time a condition is viewed, or after the item cache has been
 * invalidated by a bulk save).
 */
export const useItemValidation = (
  itemUuid: string | null | undefined,
  queryOptions?: Partial<UseQueryOptions<TemplateValidationResult, Error>>
) => {
  return useQuery<TemplateValidationResult, Error>({
    queryKey: [QueryKey.Items, itemUuid, "validation"],
    queryFn: async (): Promise<TemplateValidationResult> => {
      return await client.get(`formspec/items/${itemUuid}/validation`);
    },
    enabled: !!itemUuid,
    ...defaultQueryParams,
    ...queryOptions,
  });
};

/**
 * Hook to fetch scale options from FormSpec for Condition items.
 * Reads the allowed enum values from the Condition item type's Scale attribute
 * definition, so all options are available even before any conditions exist.
 */
export const useGetScaleOptions = <TData = { data: string[] }>(
  queryOptions?: Partial<UseQueryOptions<{ data: string[] }, Error, TData>>
) => {
  return useQuery<{ data: string[] }, Error, TData>({
    queryKey: [QueryKey.ScaleOptions],
    queryFn: async () => {
      // TODO: replace 'Condition' and 'Scale' with whatever the correct names are in prod FormSpec
      return await client.get(
        `formspec/get-attribute-enum-values?item_type_name=Condition&attribute_name=Scale`
      );
    },
    ...defaultQueryParams,
    ...queryOptions,
  });
};

/**
 * Fetch multiple templates by UUIDs from FormSpec
 * Uses individual queries for better caching - each template is cached independently
 */
export const useGetTemplates = (templateUuids: string[]) => {
  const queries = useQueries({
    queries: templateUuids.map((templateUuid) => ({
      queryKey: [QueryKey.Templates, templateUuid],
      queryFn: async () => {
        return await client.get(`formspec/templates/${templateUuid}`);
      },
      enabled: !!templateUuid,
      ...defaultQueryParams,
    })),
  });

  return {
    templates: queries.map((q) => q.data).filter((t): t is Template => !!t),
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error).filter((e): e is Error => !!e),
  };
};

/**
 * Hook to fetch templates for a specific scale from FormSpec.
 * Filtering by scale happens server-side (FormSpec matches the template's
 * base item Scale attribute).
 */
export const useGetTemplateOptionsForScale = <TData = any>(
  scale: string,
  queryOptions?: Partial<UseQueryOptions<any, Error, TData>>
) => {
  return useQuery<any, Error, TData>({
    queryKey: [QueryKey.TemplateOptions, scale],
    queryFn: async () => {
      // TODO: replace 'Condition' and 'Scale' with whatever the correct names are in prod FormSpec
      return await client.get(
        `formspec/get-template-options-by-attribute-value?item_type_name=Condition&attribute_name=Scale&attribute_value=${encodeURIComponent(
          scale
        )}`
      );
    },
    enabled: !!scale, // Only fetch when scale is provided
    ...defaultQueryParams,
    ...queryOptions,
  });
};

/**
 * Fetch multiple items by UUIDs from FormSpec
 * Uses individual queries for better caching - each item is cached independently
 */
export const useGetItems = (itemUuids: string[]) => {
  const queries = useQueries({
    queries: itemUuids.map((itemUuid) => ({
      queryKey: [QueryKey.Items, itemUuid, "details"],
      queryFn: async () => {
        const res = await client.get(`formspec/items/${itemUuid}`);
        return res;
      },
      enabled: !!itemUuid,
      ...defaultQueryParams,
    })),
  });

  return {
    items: queries
      .map((q) => q.data)
      .filter((item): item is ItemWithBOM => !!item),
    queries: queries,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error).filter((e): e is Error => !!e),
  };
};

/**
 * Fetch multiple processes (with their nested step trees) by UUIDs from FormSpec
 * Uses individual queries for better caching - each process is cached independently
 */
export const useGetProcesses = (processUuids: string[]) => {
  const queries = useQueries({
    queries: processUuids.map((processUuid) => ({
      queryKey: [QueryKey.Processes, processUuid, "with-steps"],
      queryFn: async (): Promise<ProcessWithSteps> => {
        return await client.get(
          `formspec/processes/${processUuid}?include_steps=true`
        );
      },
      enabled: !!processUuid,
      ...defaultQueryParams,
    })),
  });

  return {
    processes: queries
      .map((q) => q.data)
      .filter((data): data is ProcessWithSteps => !!data)
      .map(toProcessWithSteps),
    queries: queries,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error).filter((e): e is Error => !!e),
  };
};

export const useGetWorkOrdersFromExp = <TData = any>(
  queryOptions?: Partial<UseQueryOptions<any, Error, TData>>
) => {
  const { exp_id } = useParams();
  return useQuery<Record<string, ConditionWorkOrdersResponse[]>, Error, TData>({
    queryKey: [QueryKey.WorkOrders, exp_id],
    queryFn: async () => {
      return await client.get(`meta/experiments/${exp_id}/conditions/tasks`);
    },
    ...defaultQueryParams,
    ...queryOptions,
  });
};

/**
 * Fetch work order modification state.
 *
 * - Without `conditionId`: returns whichever condition in the experiment has an
 *   active modification (modifying/queued/failed), or null. Resolves
 *   to null once that modification finishes.
 * - With `conditionId`: returns that condition's active modification, falling
 *   back to its most recent terminal one (e.g. "submitted"). Use this to observe
 *   the outcome of a resubmission.
 */
export const useGetConditionModification = <
  TData = ConditionModification | null
>(
  conditionId?: number | null,
  queryOptions?: Partial<
    UseQueryOptions<ConditionModification | null, Error, TData>
  >
) => {
  const { exp_id } = useParams();
  // `undefined` = experiment-wide lookup; `null` = condition-scoped but not
  // yet known (query disabled, kept on its own cache key).
  const isConditionScoped = conditionId !== undefined;
  return useQuery<ConditionModification | null, Error, TData>({
    queryKey: isConditionScoped
      ? [QueryKey.WorkOrders, exp_id, "modify", conditionId]
      : [QueryKey.WorkOrders, exp_id, "modify"],
    queryFn: async () => {
      const query = isConditionScoped
        ? `?cell_condition_id=${conditionId}`
        : "";
      const response = await client.get(
        `meta/experiments/${exp_id}/conditions/modify${query}`
      );
      return response?.data ?? null;
    },
    enabled: conditionId !== null,
    ...defaultQueryParams,
    ...(isConditionScoped ? {} : { refetchOnMount: "always", staleTime: 0 }),
    ...queryOptions,
  });
};
